import { isDefined } from "@clipboard-health/util-ts";
import { put } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type TimeRange } from "@src/appV2/Shifts/Shift/types";
import {
  getExtraWorkedTime,
  getMissedPunchExtraWorkedTime,
  type MissedPunch,
} from "@src/appV2/Shifts/Shift/WorkedBreakAuthorization/utils";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

export interface SubmitShiftTimeSheetParameters {
  clockInOut: TimeRange;
  lunchInOut?: TimeRange;
  shiftId: string;
  signedBy?: string;
  breakAuthorizationReason?: string;
  missedPunches?: MissedPunch[];
  actualClockInOut?: TimeRange;
  actualLunchInOut?: TimeRange;
  location?: {
    latitude: string;
    longitude: string;
  };
  timecard?: {
    url: string;
    filename: string;
    fileStorageFileKey: string;
  };
  mandatoryBreakDurationInMinutes: number;
  authorizingEmployeeName?: string;
  authorizingEmployeeSignature?: string;
  fullShiftExtraWorkedTimeReason?: string;
}

export const DEFAULT_TIMESHEET_LOCATION = {
  latitude: "12",
  longitude: "34",
};

export function useSubmitShiftTimeSheet() {
  return useMutation({
    mutationFn: async (data: SubmitShiftTimeSheetParameters) => {
      const {
        clockInOut,
        lunchInOut,
        shiftId,
        signedBy,
        breakAuthorizationReason,
        missedPunches,
        location = DEFAULT_TIMESHEET_LOCATION,
        timecard,
        mandatoryBreakDurationInMinutes,
        authorizingEmployeeName,
        authorizingEmployeeSignature,
        fullShiftExtraWorkedTimeReason,
      } = data;

      const authorizingEmployee =
        isDefined(authorizingEmployeeName) && isDefined(authorizingEmployeeSignature)
          ? { name: authorizingEmployeeName, signature: authorizingEmployeeSignature }
          : undefined;

      // Get break extraWorkedTime
      const breakExtraWorkedTime = getExtraWorkedTime({
        mandatoryBreakDurationInMinutes,
        clockInOut,
        lunchInOut,
        breakAuthorizationReason,
        authorizingEmployee,
      });

      const missedPunchExtraWorkedTime = getMissedPunchExtraWorkedTime({
        missedPunches,
      });

      const fullShiftExtraWorkedTime =
        isDefined(fullShiftExtraWorkedTimeReason) && isDefined(data.actualClockInOut)
          ? [
              {
                type: "FULL_SHIFT" as const,
                authorization: { reason: fullShiftExtraWorkedTimeReason },
              },
            ]
          : [];

      const allExtraWorkedTime = [
        ...(breakExtraWorkedTime ?? []),
        ...missedPunchExtraWorkedTime,
        ...fullShiftExtraWorkedTime,
      ];
      const extraWorkedTime = allExtraWorkedTime.length > 0 ? allExtraWorkedTime : undefined;

      await put({
        url: `/v2/shifts/timecard/${shiftId}`,
        data: {
          ...(isDefined(timecard) && {
            timecard: [
              {
                ...timecard,
                signedType: "HCF",
                ...(isDefined(signedBy) && { digitallySignedBy: signedBy }),
              },
            ],
          }),
          location: [location.longitude, location.latitude],
          submitClockInOut: clockInOut,
          submitLunchInOut: lunchInOut,
          ...(isDefined(signedBy) && { digitallySignedBy: signedBy }),
          extraWorkedTime,
        },
        responseSchema: z.unknown(),
      });
      return timecard;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ERROR_SUBMITTING_SHIFT_TIME_SHEET,
    },
  });
}
